import { defineComponent, computed, ref, watch, onMounted } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { funcs } from '@/firebase';
export default defineComponent({
    name: 'AddUserPopup',
    props: {
        initialRole: { type: String, default: 'admin' },
        initialUser: { type: Object, required: false }
    },
    emits: ['close', 'create', 'update'],
    setup(props, { emit }) {
        const firstName = ref('');
        const lastName = ref('');
        const email = ref('');
        const password = ref('');
        const role = ref('admin');
        const companyName = ref('');
        const sendNotification = ref(false);
        const financialVisibility = ref(false); // staff/client flag
        const loading = ref(false);
        const errorMessage = ref(null);
        // Category selection (client only)
        const categories = ['Books', 'Certificates', 'Pages'];
        const selectedCategories = ref([]);
        const isEdit = ref(false);
        const populateFromInitial = (u) => {
            if (!u)
                return;
            isEdit.value = !!u.id;
            const parts = (u.displayName || '').split(' ');
            firstName.value = parts.shift() || '';
            lastName.value = parts.join(' ') || '';
            email.value = u.email || '';
            role.value = (u.role || 'client');
            companyName.value = u.companyName || '';
            sendNotification.value = !!u.sendNotification;
            financialVisibility.value = !!u.financialVisibility;
            selectedCategories.value = (u.categories || []).slice();
            photoPreview.value = u.photoURL || null;
            // do NOT populate password
            password.value = '';
        };
        onMounted(() => populateFromInitial(props.initialUser ?? null));
        watch(() => props.initialUser, (v) => populateFromInitial(v ?? null), { immediate: true });
        // always have a selected role; default to initialRole (or 'admin')
        const validRoles = new Set(['admin', 'staff', 'client']);
        // ensure staff/client specific fields are cleared when role changes
        watch(role, () => {
            financialVisibility.value = false;
            selectedCategories.value = [];
            // clear any selected avatar when switching roles
            photoPreview.value = null;
            if (fileInput.value)
                fileInput.value.value = '';
        });
        // selecting a role always sets it (no deselect -> no empty state)
        function selectRole(r) {
            role.value = r;
        }
        // toggle category membership (multi-select)
        function toggleCategory(cat) {
            const idx = selectedCategories.value.indexOf(cat);
            if (idx === -1)
                selectedCategories.value.push(cat);
            else
                selectedCategories.value.splice(idx, 1);
        }
        // Securely generate an 8-character password containing upper, lower, digits and symbols
        function generatePassword() {
            const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const lower = 'abcdefghijklmnopqrstuvwxyz';
            const digits = '0123456789';
            const symbols = '!@$^&*?';
            const all = upper + lower + digits + symbols;
            const pick = (chars) => {
                const rnd = crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000;
                return chars[Math.floor(rnd * chars.length)];
            };
            let out = '';
            out += pick(upper);
            out += pick(lower);
            out += pick(digits);
            out += pick(symbols);
            while (out.length < 8)
                out += pick(all);
            // shuffle
            const arr = out.split('');
            for (let i = arr.length - 1; i > 0; i--) {
                const rnd = crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000;
                const j = Math.floor(rnd * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }
            password.value = arr.join('');
            try {
                navigator.clipboard?.writeText(password.value);
            }
            catch { }
        }
        const title = computed(() => {
            if (!role.value)
                return 'Creating User';
            const label = role.value === 'admin' ? 'Admin' : role.value === 'staff' ? 'Staff' : 'Client';
            return `Creating ${label} User`;
        });
        function onClose() {
            resetForm();
            emit('close');
        }
        async function onSubmit() {
            errorMessage.value = null;
            // client-side required validation
            if (!firstName.value.trim()) {
                errorMessage.value = 'Name is required.';
                return;
            }
            if (!lastName.value.trim()) {
                errorMessage.value = 'Surname is required.';
                return;
            }
            if (!email.value.trim()) {
                errorMessage.value = 'Email is required.';
                return;
            }
            if (!password.value || password.value.length < 6) {
                errorMessage.value = 'Password is required (min 6 chars).';
                return;
            }
            if (!companyName.value.trim()) {
                errorMessage.value = 'Company Name is required.';
                return;
            }
            loading.value = true;
            try {
                const payload = {
                    email: email.value.trim(),
                    displayName: `${firstName.value.trim()} ${lastName.value.trim()}`,
                    role: role.value || 'client',
                    companyName: companyName.value.trim(),
                    sendNotification: !!sendNotification.value,
                    financialVisibility: !!financialVisibility.value,
                    categories: selectedCategories.value.slice(),
                    password: password.value,
                    photoBase64: photoPreview.value ?? null
                };
                if (isEdit.value && props.initialUser?.id) {
                    // editing — emit update and let parent write to Firestore/Auth as needed
                    emit('update', { id: props.initialUser.id, ...payload });
                    emit('close');
                    return;
                }
                // creating — call createUser callable as before
                const createUserFn = httpsCallable(funcs, 'createUserAndWelcome');
                const res = await createUserFn(payload);
                const result = res.data;
                emit('create', result);
                if (result?.warning) {
                    window.alert(result.warning);
                }
                else {
                    window.alert('The account was created and the welcome email was sent.');
                }
                emit('close');
            }
            catch (err) {
                // full debug output to console
                console.error('create user failed (full):', err);
                try {
                    console.log('err.code:', err?.code, 'err.message:', err?.message);
                }
                catch { }
                try {
                    console.log('err.details:', err?.details);
                }
                catch { }
                try {
                    console.dir(err);
                }
                catch { }
                // print server debug logs if included in error.details
                if (err?.details?.debugLogs) {
                    console.groupCollapsed('server debug logs (error.details)');
                    (err.details.debugLogs || []).forEach((line) => console.log(line));
                    console.groupEnd();
                }
                if (err?.code === 'functions/permission-denied' || String(err?.message).toLowerCase().includes('permission')) {
                    errorMessage.value = 'You do not have permission to create users.';
                }
                else if (err?.code === 'auth/email-already-exists' || String(err?.message).toLowerCase().includes('already')) {
                    errorMessage.value = 'An account with that email already exists.';
                }
                else {
                    errorMessage.value = err?.message || 'Failed to create user. Try again.';
                }
            }
            finally {
                loading.value = false;
            }
        }
        function resetForm() {
            firstName.value = '';
            lastName.value = '';
            email.value = '';
            password.value = '';
            companyName.value = '';
            sendNotification.value = false;
            role.value = 'admin';
            financialVisibility.value = false;
            selectedCategories.value = [];
            photoPreview.value = null;
            if (fileInput.value)
                fileInput.value.value = '';
        }
        // image upload / preview state
        const photoPreview = ref(null);
        const fileInput = ref(null);
        function triggerFileInput() {
            fileInput.value?.click();
        }
        function onFileChange(e) {
            const input = e.target;
            const file = input?.files?.[0];
            if (!file)
                return;
            // optional: validate size/type here (e.g. file.size, file.type)
            const reader = new FileReader();
            reader.onload = () => {
                photoPreview.value = String(reader.result || null);
            };
            reader.readAsDataURL(file);
        }
        return {
            loading, errorMessage,
            firstName, lastName, email, password, companyName, sendNotification,
            financialVisibility,
            categories, selectedCategories, toggleCategory,
            role, title, onClose, onSubmit, generatePassword, selectRole,
            photoPreview, fileInput, triggerFileInput, onFileChange, isEdit
        };
    }
});
